// Face-normalised placement of people on the shared stage.
//
// Every person is scaled so their face has the same height on the stage,
// regardless of how close they sit to their own camera. That removes the
// "giant next to a tiny person" effect when two remote people are combined.

/** Face box in normalised source-frame coords (0..1), un-mirrored. */
export interface FaceBox {
  cx: number;
  cy: number;
  h: number;
}

/** Per-person manual adjustment, in stage-relative units. */
export interface PoseAdjust {
  dx: number; // fraction of stage width
  dy: number; // fraction of stage height
  zoom: number; // multiplier on the normalised size
}

export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export const DEFAULT_FACE: FaceBox = { cx: 0.5, cy: 0.38, h: 0.24 };
export const NO_ADJUST: PoseAdjust = { dx: 0, dy: 0, zoom: 1 };

/** Target face height as a fraction of stage height. */
export const TARGET_FACE = 0.2;
/** Vertical position of face centres on stage. */
export const FACE_LINE = 0.36;

/** Horizontal slot centre for person `i` of `n` on stage. */
export function slotX(i: number, n: number) {
  if (n <= 1) return 0.5;
  const spread = Math.min(0.8, 0.4 * (n - 1));
  const left = 0.5 - spread / 2;
  return left + (spread * i) / (n - 1);
}

/** Overrides for the normalisation target (used when matching a member's real scene). */
export interface PlaceTarget {
  /** face height as fraction of stage height */
  face: number;
  /** face centre y as fraction of stage height */
  line: number;
}

/**
 * Destination rect (stage pixels) for a source frame of size srcW×srcH.
 * `mirrored`: the layer is drawn horizontally flipped (selfie view), so the
 * face's x position must be flipped too.
 */
export function placePerson(
  stageW: number,
  stageH: number,
  srcW: number,
  srcH: number,
  face: FaceBox,
  slot: number,
  adj: PoseAdjust = NO_ADJUST,
  mirrored = true,
  target: PlaceTarget = { face: TARGET_FACE, line: FACE_LINE },
): Rect {
  const faceH = Math.max(0.05, Math.min(0.9, face.h));
  const zoom = Math.max(0.5, Math.min(2, adj.zoom));
  // stage pixels per source pixel
  let s = (target.face * stageH * zoom) / (faceH * srcH);
  // never upscale more than 3x the "fit height" scale, nor shrink below 0.25x
  const fit = stageH / srcH;
  s = Math.max(fit * 0.25, Math.min(fit * 3, s));
  const w = srcW * s;
  const h = srcH * s;
  const fx = mirrored ? 1 - face.cx : face.cx;
  const x = (slot + adj.dx) * stageW - fx * w;
  const y = (target.line + adj.dy) * stageH - face.cy * h;
  return { x, y, w, h };
}

/** Rect that covers the whole stage (object-fit: cover). */
export function coverRect(stageW: number, stageH: number, srcW: number, srcH: number): Rect {
  const s = Math.max(stageW / srcW, stageH / srcH);
  const w = srcW * s, h = srcH * s;
  return { x: (stageW - w) / 2, y: (stageH - h) / 2, w, h };
}

export interface PersonInput {
  srcW: number;
  srcH: number;
  face: FaceBox;
  adjust: PoseAdjust;
  /** 0 = host, 1.. = guests */
  slot: number;
  /** front camera (selfie, drawn mirrored) – false for the rear camera */
  mirrored?: boolean;
  /** no face visible (e.g. rear camera filming only the scenery) */
  noFace?: boolean;
}

export interface Placed {
  rect: Rect;
  /** false → draw the full camera frame (it *is* the background) */
  masked: boolean;
  mirrored: boolean;
}

/**
 * Lays out everybody on the stage. Shared by the live WebGL stage and by the
 * strip renderer (browser + server), so the photo always matches the preview.
 *
 * sceneSlot: that member's real surroundings are the backdrop ("come into my
 * scene" – e.g. a friend travelling). Their camera frame is drawn full-bleed
 * (not cut out) and everybody else is scaled to *their* face size and placed
 * next to them. If the scene shows no face (rear camera), the others are laid
 * out with the normal size target instead.
 */
export function arrange(
  stageW: number,
  stageH: number,
  people: PersonInput[],
  sceneSlot: number | null = null,
): Placed[] {
  const mir = (p: PersonInput) => p.mirrored !== false;
  const scene = sceneSlot == null ? undefined : people.find((p) => p.slot === sceneSlot);
  const others = people.filter((p) => p !== scene).sort((a, b) => a.slot - b.slot);
  const n = Math.max(1, others.length);
  const normal = (p: PersonInput, target?: PlaceTarget, x?: number): Placed => ({
    rect: placePerson(stageW, stageH, p.srcW, p.srcH, p.face, x ?? slotX(others.indexOf(p), n), p.adjust, mir(p), target),
    masked: true,
    mirrored: mir(p),
  });
  if (!scene) return people.map((p) => normal(p));

  const bg = coverRect(stageW, stageH, scene.srcW, scene.srcH);
  const backdrop: Placed = { rect: bg, masked: false, mirrored: mir(scene) };
  if (scene.noFace) return people.map((p) => (p === scene ? backdrop : normal(p)));

  const fcx = mir(scene) ? 1 - scene.face.cx : scene.face.cx;
  const sceneFaceX = (bg.x + fcx * bg.w) / stageW;
  const target: PlaceTarget = {
    face: Math.max(0.08, Math.min(0.45, (scene.face.h * bg.h) / stageH)),
    line: Math.max(0.15, Math.min(0.75, (bg.y + scene.face.cy * bg.h) / stageH)),
  };
  const side = sceneFaceX < 0.5 ? 1 : -1;
  return people.map((p) => {
    if (p === scene) return backdrop;
    const k = others.indexOf(p) + 1;
    return normal(p, target, Math.max(0.12, Math.min(0.88, sceneFaceX + side * 0.28 * k)));
  });
}

/** Which member's camera is the backdrop for a given config (null = virtual background). */
export function sceneSlotOf(cfg: { background: string; sceneSlot?: number }): number | null {
  if (cfg.background === 'member-camera') return cfg.sceneSlot ?? 0;
  if (cfg.background === 'host-camera') return 0; // legacy v0.2 sessions
  return null;
}

/** Exponential smoothing for face boxes so the layout does not jitter. */
export function smoothFace(prev: FaceBox | null, next: FaceBox, alpha = 0.25): FaceBox {
  if (!prev) return next;
  // big jumps (person moved quickly / re-detected) snap faster
  const jump = Math.abs(prev.cx - next.cx) + Math.abs(prev.cy - next.cy) > 0.25;
  const a = jump ? 0.6 : alpha;
  return {
    cx: prev.cx + (next.cx - prev.cx) * a,
    cy: prev.cy + (next.cy - prev.cy) * a,
    h: prev.h + (next.h - prev.h) * a * 0.6,
  };
}

// ====================================================================== v0.5
// Eye-line + IPD placement (SA B2): a similarity transform per person
//   stage = R(rot) · s · (src − anchor_src) + anchor_stage
// that puts the midpoint between the eyes on the slot's eye line and scales the
// inter-pupil distance to a common reference (63 mm on everybody), with a small
// roll compensation. All coordinates are UN-MIRRORED stage space; the stage is
// mirrored as a whole only when displayed / saved (render/stage.ts, strip.ts).

/** Eye anchor in source-normalised coords (un-mirrored). `ipd` = inter-pupil distance / source height. */
export interface EyeAnchor {
  ex: number;
  ey: number;
  ipd: number;
  /** head roll (rad), 0 = level */
  roll: number;
  /**
   * horizontal body anchor (shoulder midpoint x, source-normalised) when the shoulders are
   * visible: the slot pins the BODY horizontally, so leaning the head (P2) actually moves
   * the head toward the partner instead of sliding the whole person away.
   */
  bx?: number;
}

/** On-stage inter-pupil distance (fraction of stage height) ≈ 63 mm reference. */
export const TARGET_IPD = TARGET_FACE / 3.4;
/** Eye line on stage (fraction of stage height). */
export const EYE_LINE = FACE_LINE - 0.05 * TARGET_FACE;
/** Roll compensation fades out between these angles (SA B2: only below 8 degrees). */
export const ROLL_FULL = (5 * Math.PI) / 180;
export const ROLL_MAX = (8 * Math.PI) / 180;

/** Legacy face box ↔ eye anchor (exact inverses, so old cut-out metas place identically). */
export function anchorFromFace(f: FaceBox): EyeAnchor {
  const h = Math.max(0.02, f.h);
  return { ex: f.cx, ey: f.cy - 0.05 * h, ipd: h / 3.4, roll: 0 };
}
export function faceFromAnchor(a: EyeAnchor): FaceBox {
  const h = a.ipd * 3.4;
  return { cx: a.ex, cy: a.ey + 0.05 * h, h };
}

export interface Placement {
  /** stage px per source px */
  s: number;
  /** rotation (rad) about the anchor */
  rot: number;
  /** anchor in source px */
  ax: number;
  ay: number;
  /** anchor in stage px */
  px: number;
  py: number;
  /** false → full camera frame used as the backdrop (not cut out) */
  masked: boolean;
  /** axis-aligned bounds of the transformed source frame (stage px) */
  rect: Rect;
}

/** Source px → stage px. */
export function toStage(p: Placement, sx: number, sy: number): [number, number] {
  const c = Math.cos(p.rot), s = Math.sin(p.rot);
  const dx = (sx - p.ax) * p.s, dy = (sy - p.ay) * p.s;
  return [p.px + c * dx - s * dy, p.py + s * dx + c * dy];
}

/** Stage px → source px. */
export function toSource(p: Placement, x: number, y: number): [number, number] {
  const c = Math.cos(p.rot), s = Math.sin(p.rot);
  const dx = x - p.px, dy = y - p.py;
  return [p.ax + (c * dx + s * dy) / p.s, p.ay + (-s * dx + c * dy) / p.s];
}

/** 2×3 affine [a, b, c, d, e, f] (Canvas2D setTransform order): x' = a·x + c·y + e, y' = b·x + d·y + f. */
export function placementMatrix(p: Placement): [number, number, number, number, number, number] {
  const c = Math.cos(p.rot) * p.s, s = Math.sin(p.rot) * p.s;
  return [c, s, -s, c, p.px - c * p.ax + s * p.ay, p.py - s * p.ax - c * p.ay];
}

function boundsOf(p: Placement, srcW: number, srcH: number): Rect {
  const pts = [toStage(p, 0, 0), toStage(p, srcW, 0), toStage(p, 0, srcH), toStage(p, srcW, srcH)];
  const xs = pts.map((q) => q[0]), ys = pts.map((q) => q[1]);
  const x = Math.min(...xs), y = Math.min(...ys);
  return { x, y, w: Math.max(...xs) - x, h: Math.max(...ys) - y };
}

/** Roll compensation: full below 5 degrees, fades to none at 8 (a deliberate head tilt is kept). */
export function rollCompensation(roll: number) {
  const a = Math.abs(roll);
  if (a <= ROLL_FULL) return -roll;
  if (a >= ROLL_MAX) return 0;
  const t = (a - ROLL_FULL) / (ROLL_MAX - ROLL_FULL);
  return -roll * (1 - t * t * (3 - 2 * t));
}

export interface EyeTarget {
  /** on-stage IPD (fraction of stage height) */
  ipd: number;
  /** eye line (fraction of stage height) */
  line: number;
}

/**
 * Similarity placement of one person. `slotX` is in UN-MIRRORED stage space (0..1).
 * `adj.dx` is a display-space drag offset, so it is negated when the stage is mirrored.
 */
export function placeByEyes(
  stageW: number, stageH: number, srcW: number, srcH: number,
  a: EyeAnchor, slotX: number, adj: PoseAdjust = NO_ADJUST, mirror = true,
  target: EyeTarget = { ipd: TARGET_IPD, line: EYE_LINE }, extraZoom = 1, natural = false,
): Placement {
  if (natural) return placeNatural(stageW, stageH, srcW, srcH, slotX, adj, mirror);
  const ipd = Math.max(0.006, Math.min(0.4, a.ipd));
  const zoom = Math.max(0.5, Math.min(2, adj.zoom)) * extraZoom;
  let s = (target.ipd * stageH * zoom) / (ipd * srcH);
  const fit = stageH / srcH;
  s = Math.max(fit * 0.25, Math.min(fit * 3, s));
  const p: Placement = {
    s, rot: rollCompensation(a.roll),
    ax: (a.bx ?? a.ex) * srcW, ay: a.ey * srcH,
    px: (slotX + (mirror ? -adj.dx : adj.dx)) * stageW,
    py: (target.line + adj.dy) * stageH,
    masked: true, rect: { x: 0, y: 0, w: 0, h: 0 },
  };
  p.rect = boundsOf(p, srcW, srcH);
  return p;
}

/**
 * v0.7 "tỉ lệ thật": every camera frame covers the stage at the same scale, nobody is
 * resized — whoever stands closer to their camera looks bigger (and stands in front,
 * see the solver), stepping back makes you smaller. The frame is only translated
 * sideways so the frame centre sits on the person's slot; moving inside your own
 * camera moves you on the stage one-to-one.
 */
export function placeNatural(
  stageW: number, stageH: number, srcW: number, srcH: number, slotX: number, adj: PoseAdjust = NO_ADJUST, mirror = true,
): Placement {
  const cover = Math.max(stageW / srcW, stageH / srcH);
  const zoom = Math.max(0.5, Math.min(2, adj.zoom));
  const p: Placement = {
    s: cover * zoom, rot: 0,
    ax: srcW / 2, ay: srcH / 2,
    px: (slotX + (mirror ? -adj.dx : adj.dx)) * stageW,
    py: (0.5 + adj.dy) * stageH,
    masked: true, rect: { x: 0, y: 0, w: 0, h: 0 },
  };
  p.rect = boundsOf(p, srcW, srcH);
  return p;
}

export interface PlaceInput {
  srcW: number;
  srcH: number;
  anchor: EyeAnchor;
  adjust: PoseAdjust;
  /** slot centre, UN-MIRRORED stage space 0..1 */
  slotX: number;
  /** this member's camera is the backdrop */
  scene?: boolean;
  noFace?: boolean;
  /** depth-driven perspective zoom (at most 1.1, SA B5.1) */
  extraZoom?: number;
  /** v0.7: real scale (no face normalisation) */
  natural?: boolean;
}

/**
 * Places everybody (live stage and still renderer share this, so the saved
 * photo matches the mirror). With a scene backdrop the others are scaled to the
 * scene person's eye size and put next to them, as before.
 */
export function placeAll(stageW: number, stageH: number, people: PlaceInput[], mirror = true): Placement[] {
  const scene = people.find((p) => p.scene);
  const place = (p: PlaceInput, slotX = p.slotX, target?: EyeTarget) => anchorBottom(
    placeByEyes(stageW, stageH, p.srcW, p.srcH, p.anchor, slotX, p.adjust, mirror, target, p.extraZoom ?? 1, !!p.natural && !target),
    p.srcW, p.srcH, stageH);
  if (!scene) return people.map((p) => place(p));
  const bg = coverRect(stageW, stageH, scene.srcW, scene.srcH);
  const backdrop: Placement = { s: bg.w / scene.srcW, rot: 0, ax: 0, ay: 0, px: bg.x, py: bg.y, masked: false, rect: bg };
  if (scene.noFace) return people.map((p) => (p === scene ? backdrop : place(p)));
  const a = scene.anchor;
  const sceneX = (bg.x + a.ex * bg.w) / stageW; // un-mirrored stage x of the scene person's eyes
  const target: EyeTarget = {
    ipd: Math.max(0.025, Math.min(0.13, (a.ipd * bg.h) / stageH)),
    line: Math.max(0.12, Math.min(0.72, (bg.y + a.ey * bg.h) / stageH)),
  };
  const others = people.filter((p) => p !== scene);
  // pick the display-space side with more room, then convert back to stage space
  const dispX = mirror ? 1 - sceneX : sceneX;
  const side = dispX < 0.5 ? 1 : -1;
  return people.map((p) => {
    if (p === scene) return backdrop;
    const k = others.indexOf(p) + 1;
    const d = Math.max(0.12, Math.min(0.88, dispX + side * 0.28 * k));
    return frameCover(place(p, mirror ? 1 - d : d, target), p, stageW, stageH);
  });
}

/**
 * v0.9.5: in a real-room scene, a person's camera frame is never moved sideways or shrunk — it
 * covers the picture like the owner's frame (centred, standing on the bottom edge), so no edge
 * of it can cut through a body inside the picture. Everybody stands where they stand in their
 * own camera (the shared mirror shows exactly that). The head-size match only ever zooms IN.
 */
export function frameCover(matched: Placement, p: PlaceInput, stageW: number, stageH: number): Placement {
  const cover = Math.max(stageW / p.srcW, stageH / p.srcH);
  const s = Math.max(cover, Math.min(cover * 1.6, matched.s)) * Math.max(1, Math.min(1.6, p.adjust.zoom));
  return {
    s, rot: 0, ax: p.srcW / 2, ay: p.srcH, px: stageW / 2, py: stageH, masked: true,
    rect: { x: stageW / 2 - (p.srcW * s) / 2, y: stageH - p.srcH * s, w: p.srcW * s, h: p.srcH * s },
  };
}

/**
 * v0.9.3: a cut-out person stands on the bottom of the picture. A selfie frame cuts the body off
 * at its bottom edge; when that edge ends up INSIDE the stage (frame moved up or shrunk), the
 * straight cut floats mid-picture and gives the composite away. The person is moved down until
 * the cut lies at (or below) the stage's bottom edge — at most 25 % of the stage height.
 */
export function anchorBottom(p: Placement, srcW: number, srcH: number, stageH: number): Placement {
  if (!p.masked) return p;
  const y = Math.min(toStage(p, 0, srcH)[1], toStage(p, srcW, srcH)[1]);
  const gap = stageH - y;
  if (gap <= 0.5) return p;
  return { ...p, py: p.py + Math.min(gap, 0.25 * stageH) };
}

/** Display-space x (what the user sees) ↔ un-mirrored stage x. Its own inverse. */
export const stageXOf = (displayX: number, mirror: boolean) => (mirror ? 1 - displayX : displayX);

// ---------------------------------------------------------------- One-Euro filter

/** Casiez et al. 1-euro filter: smooth when still, low lag when moving. */
export class OneEuro {
  private x: number | null = null;
  private dx = 0;
  private t = 0;
  constructor(private minCutoff = 1.2, private beta = 4, private dCutoff = 1) {}

  private static alpha(cutoff: number, dt: number) {
    const tau = 1 / (2 * Math.PI * cutoff);
    return 1 / (1 + tau / dt);
  }

  filter(v: number, tMs: number): number {
    if (this.x == null) { this.x = v; this.t = tMs; return v; }
    const dt = Math.max(1e-3, (tMs - this.t) / 1000);
    this.t = tMs;
    const d = (v - this.x) / dt;
    this.dx += OneEuro.alpha(this.dCutoff, dt) * (d - this.dx);
    const cutoff = this.minCutoff + this.beta * Math.abs(this.dx);
    this.x += OneEuro.alpha(cutoff, dt) * (v - this.x);
    return this.x;
  }

  reset() { this.x = null; this.dx = 0; }
  get value() { return this.x; }
}

/** One-Euro filtering of an eye anchor (IPD filtered in log space). */
export class AnchorFilter {
  private fx = new OneEuro(1.2, 4);
  private fy = new OneEuro(1.2, 4);
  private fi = new OneEuro(0.8, 2);
  private fr = new OneEuro(1, 1);
  private fb = new OneEuro(1.2, 4);
  private last: EyeAnchor | null = null;

  filter(a: EyeAnchor, tMs: number): EyeAnchor {
    // a big jump (re-detection) snaps instead of sliding across the stage
    if (this.last && Math.hypot(a.ex - this.last.ex, a.ey - this.last.ey) > 0.3) this.reset();
    this.last = {
      ex: this.fx.filter(a.ex, tMs),
      ey: this.fy.filter(a.ey, tMs),
      ipd: Math.exp(this.fi.filter(Math.log(Math.max(1e-3, a.ipd)), tMs)),
      roll: this.fr.filter(a.roll, tMs),
    };
    if (a.bx != null) this.last.bx = this.fb.filter(a.bx, tMs);
    else this.fb.reset();
    return this.last;
  }

  reset() { this.fx.reset(); this.fy.reset(); this.fi.reset(); this.fr.reset(); this.fb.reset(); this.last = null; }
  get value() { return this.last; }
}

// ---------------------------------------------------------------- virtual camera (v0.7.1)
//
// Real scale keeps everybody's relative size, but a person standing far from their phone
// ends up tiny in an empty stage. A virtual camera frames the GROUP: one zoom for everybody
// (relative sizes stay true), the group centred, feet on the stage floor (or, when the
// bodies are cut by the cameras, a little head room). The stage smooths it over ~1 s;
// the still uses the view of the shutter instant.

/**
 * Body box (stage px) from source-px capsule bounds [x0, y0, x1, y1], or the whole placed
 * frame when there is no pose (that person then limits the zoom).
 */
export function bodyBox(p: Placement, srcBox: [number, number, number, number] | null): Rect {
  if (!srcBox) return p.rect;
  const [x0, y0, x1, y1] = srcBox;
  const pts = [toStage(p, x0, y0), toStage(p, x1, y0), toStage(p, x0, y1), toStage(p, x1, y1)];
  const xs = pts.map((q) => q[0]), ys = pts.map((q) => q[1]);
  return { x: Math.min(...xs), y: Math.min(...ys), w: Math.max(...xs) - Math.min(...xs), h: Math.max(...ys) - Math.min(...ys) };
}

/** stage' = (stage − centre)·z + centre + (tx·W, ty·H) */
export interface GroupView { z: number; tx: number; ty: number }
export const NO_VIEW: GroupView = { z: 1, tx: 0, ty: 0 };

export function applyView(p: Placement, v: GroupView, w: number, h: number, srcW: number, srcH: number): Placement {
  if (v.z === 1 && v.tx === 0 && v.ty === 0) return p;
  const q: Placement = {
    ...p, s: p.s * v.z,
    px: (p.px - w / 2) * v.z + w / 2 + v.tx * w,
    py: (p.py - h / 2) * v.z + h / 2 + v.ty * h,
  };
  q.rect = boundsOf(q, srcW, srcH);
  return q;
}

/**
 * Target view for the union of the people's body boxes (stage px at zoom 1). Zoom only in
 * (1..2.5): never shrink people below their real scale.
 */
export function groupView(boxes: Rect[], w: number, h: number): GroupView {
  if (!boxes.length) return NO_VIEW;
  const x0 = Math.min(...boxes.map((b) => b.x)), x1 = Math.max(...boxes.map((b) => b.x + b.w));
  const y0 = Math.min(...boxes.map((b) => b.y)), y1 = Math.max(...boxes.map((b) => b.y + b.h));
  const uw = Math.max(1, x1 - x0), uh = Math.max(1, y1 - y0);
  const z = Math.max(1, Math.min(2.5, (0.86 * h) / uh, (0.92 * w) / uw));
  const tx = -((x0 + x1) / 2 - w / 2) * z / w;
  const cut = y1 >= 0.98 * h; // bodies run out of the cameras at the bottom
  const ty = cut
    ? (0.06 * h - ((y0 - h / 2) * z + h / 2)) / h       // head room on top
    : (0.97 * h - ((y1 - h / 2) * z + h / 2)) / h;      // feet on the floor
  return { z, tx, ty };
}

/** Exponential approach of the view (dt in ms, ~0.8 s time constant). */
export function smoothView(cur: GroupView, target: GroupView, dtMs: number): GroupView {
  const a = 1 - Math.exp(-Math.max(0, dtMs) / 800);
  return { z: cur.z + (target.z - cur.z) * a, tx: cur.tx + (target.tx - cur.tx) * a, ty: cur.ty + (target.ty - cur.ty) * a };
}
